import React from "react";
import { apiClient } from "../lib/api";
import { useZones, invalidateZonesCache } from "../hooks/useZones";

const BASE_STYLE = {
  display: "inline-flex", alignItems: "center", gap: 4,
  fontSize: 10, fontWeight: 800, letterSpacing: "0.03em", textTransform: "uppercase",
  borderRadius: 999, padding: "2px 7px", lineHeight: 1.4, whiteSpace: "nowrap",
};

/** Small, consistent zone tag — used wherever a VA already picks a provider
 * (Kaizen shortlist, TP broadcast drawer, CPD assignment list) and on the
 * Floor map. Inline-styled so it drops cleanly into any host screen's
 * existing look (dark dispatch console or light onboarding forms) without
 * needing that screen's stylesheet. */
export function ZoneBadge({ zoneName, sameZone, style }) {
  if (!zoneName) return null;
  const colors = sameZone
    ? { background: "rgba(127,216,183,0.18)", color: "#2f9e73", border: "1px solid rgba(127,216,183,0.4)" }
    : { background: "rgba(148,163,184,0.15)", color: "#64748b", border: "1px solid rgba(148,163,184,0.3)" };
  return (
    <span
      style={{ ...BASE_STYLE, ...colors, ...style }}
      data-testid="zone-badge"
      title={sameZone ? `Same zone as pickup (${zoneName})` : `Zone: ${zoneName}`}
    >
      {zoneName}
    </span>
  );
}

export function SameZoneToggle({ checked, onChange, testId = "same-zone-toggle", style }) {
  return (
    <label
      data-testid={testId}
      style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11, fontWeight: 600, cursor: "pointer", userSelect: "none", ...style }}
    >
      <input type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} style={{ cursor: "pointer" }} />
      <span>same zone only</span>
    </label>
  );
}

/** Admin override — pin a driver/TP to a specific zone regardless of their
 * address, or clear the override to go back to auto-tagging. */
export function ZoneOverrideSelect({ entityType, entityId, zoneId, zoneName, zoneSource, onSaved, testId = "zone-override-select" }) {
  const [saving, setSaving] = React.useState(false);
  const { zones } = useZones();

  const save = async (newZoneId) => {
    setSaving(true);
    try {
      await apiClient.post("/zones/assign", { entity_type: entityType, entity_id: entityId, zone_id: newZoneId || null });
      invalidateZonesCache();
      onSaved?.();
    } finally {
      setSaving(false);
    }
  };

  return (
    <label style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: "0.08em", color: "#64748B" }}>
        Zone {zoneSource === "manual" ? "(manually set)" : "(auto from address)"}
      </span>
      <select
        value={zoneId || ""}
        onChange={(e) => save(e.target.value)}
        disabled={saving}
        data-testid={testId}
        style={{ border: "1px solid #E2E0D8", borderRadius: 6, padding: "6px 8px", fontSize: 13 }}
      >
        <option value="">Unassigned — auto-detect from address</option>
        {zones.map((z) => (
          <option key={z.id} value={z.id}>{z.name}{z.id === zoneId ? " ✓" : ""}</option>
        ))}
      </select>
    </label>
  );
}
